/**
 * Plan Quality Validation System
 * 
 * Validates generated emergency plans meet professional quality standards
 */

export interface SectionValidation {
  section: string;
  complete: boolean;
  issues: string[];
  pageEstimate: number;
}

export interface PlanQualityReport {
  passed: boolean;
  overallScore: number;
  pageCount: number;
  pageCountTarget: number;
  emergencyTypesCovered: number;
  emergencyTypesRequired: number;
  readinessScore: number;
  sectionsComplete: SectionValidation[];
  crossReferenceErrors: string[];
  recommendations: string[];
  timestamp: string;
}

const REQUIRED_SECTIONS = [
  { name: 'Cover + Disclaimer', minPages: 2, required: true },
  { name: 'Executive Summary', minPages: 2, required: true },
  { name: 'Household Profile', minPages: 2, required: true },
  { name: 'Risk Assessment', minPages: 4, required: true },
  { name: 'Emergency Services', minPages: 3, required: true },
  { name: 'Emergency Procedures', minPages: 6, required: true },
  { name: 'Evacuation Plan', minPages: 3, required: false },
  { name: 'Communication Plan', minPages: 2, required: false },
  { name: 'Supplies Checklist', minPages: 3, required: true },
  { name: 'Financial Preparedness', minPages: 1, required: true },
  { name: 'Recovery Planning', minPages: 2, required: true },
  { name: 'Monthly Calendar', minPages: 1, required: true },
  { name: 'Quick Reference', minPages: 1, required: true },
  { name: 'Wallet Cards', minPages: 1, required: true },
  { name: 'Local Resources', minPages: 2, required: true },
];

const PAGE_COUNT_TARGET = 30;
const MINIMUM_EMERGENCY_TYPES = 6;

export function validatePlanQuality(compiledPlan: any, interview: any): PlanQualityReport {
  const sections: SectionValidation[] = [];
  const crossReferenceErrors: string[] = [];
  const recommendations: string[] = [];
  
  let totalPageEstimate = 0;
  
  // Validate Cover + Disclaimer
  const coverSection = validateCoverSection(compiledPlan);
  sections.push(coverSection);
  totalPageEstimate += coverSection.pageEstimate;
  
  // Validate Executive Summary
  const summarySection = validateExecutiveSummary(compiledPlan);
  sections.push(summarySection);
  totalPageEstimate += summarySection.pageEstimate;
  
  // Validate Household Profile
  const householdSection = validateHouseholdProfile(compiledPlan, interview);
  sections.push(householdSection);
  totalPageEstimate += householdSection.pageEstimate;
  
  // Validate Risk Assessment
  const riskSection = validateRiskAssessment(compiledPlan);
  sections.push(riskSection);
  totalPageEstimate += riskSection.pageEstimate;
  
  // Validate Emergency Services
  const servicesSection = validateEmergencyServices(compiledPlan);
  sections.push(servicesSection);
  totalPageEstimate += servicesSection.pageEstimate;
  
  // Validate Emergency Procedures
  const proceduresSection = validateEmergencyProcedures(compiledPlan, interview);
  sections.push(proceduresSection);
  totalPageEstimate += proceduresSection.pageEstimate;
  
  // Validate Supplies Checklist
  const suppliesSection = validateSuppliesChecklist(compiledPlan);
  sections.push(suppliesSection);
  totalPageEstimate += suppliesSection.pageEstimate;
  
  // Validate Financial Preparedness
  const financialSection = validateFinancialSection(compiledPlan);
  sections.push(financialSection);
  totalPageEstimate += financialSection.pageEstimate;
  
  // Validate Recovery Planning
  const recoverySection = validateRecoverySection(compiledPlan);
  sections.push(recoverySection);
  totalPageEstimate += recoverySection.pageEstimate;
  
  // Validate Calendar, Quick Reference, Wallet Cards
  const calendarSection = validateCalendarSection(compiledPlan);
  sections.push(calendarSection);
  totalPageEstimate += calendarSection.pageEstimate;
  
  const quickRefSection = validateQuickReference(compiledPlan);
  sections.push(quickRefSection);
  totalPageEstimate += quickRefSection.pageEstimate;
  
  const walletSection = validateWalletCards(compiledPlan);
  sections.push(walletSection);
  totalPageEstimate += walletSection.pageEstimate;
  
  const localResourcesSection = validateLocalResources(compiledPlan);
  sections.push(localResourcesSection);
  totalPageEstimate += localResourcesSection.pageEstimate;
  
  // Cross-reference validation
  const xrefErrors = validateCrossReferences(compiledPlan);
  crossReferenceErrors.push(...xrefErrors);
  
  // Generate recommendations
  const recs = generateRecommendations(sections, totalPageEstimate, interview);
  recommendations.push(...recs);
  
  // Calculate emergency types coverage
  const emergencyTypesCovered = compiledPlan?.emergency_procedures_deep?.length || 
    compiledPlan?.emergency_procedures?.length || 0;
  const emergencyTypesRequired = interview?.emergency_types?.length || MINIMUM_EMERGENCY_TYPES;
  
  // Calculate readiness score
  const readinessScore = compiledPlan?.metadata?.readiness_score || 
    calculateReadinessScore(sections, emergencyTypesCovered, emergencyTypesRequired);
  
  // Calculate overall score
  const completeSections = sections.filter(s => s.complete).length;
  const overallScore = Math.round((completeSections / sections.length) * 100);
  
  // Determine pass/fail
  const passed = totalPageEstimate >= PAGE_COUNT_TARGET && 
    emergencyTypesCovered >= MINIMUM_EMERGENCY_TYPES &&
    overallScore >= 70;
  
  return {
    passed,
    overallScore,
    pageCount: totalPageEstimate,
    pageCountTarget: PAGE_COUNT_TARGET,
    emergencyTypesCovered,
    emergencyTypesRequired,
    readinessScore,
    sectionsComplete: sections,
    crossReferenceErrors,
    recommendations,
    timestamp: new Date().toISOString(),
  };
}

function validateCoverSection(plan: any): SectionValidation {
  const issues: string[] = [];
  
  if (!plan?.metadata?.plan_title) {
    issues.push('Missing plan title');
  }
  if (!plan?.legal_disclaimer?.paragraphs?.length) {
    issues.push('Missing legal disclaimer');
  }
  
  return {
    section: 'Cover + Disclaimer',
    complete: issues.length === 0,
    issues,
    pageEstimate: 2,
  };
}

function validateExecutiveSummary(plan: any): SectionValidation {
  const issues: string[] = [];
  const summary = plan?.executive_summary;
  
  if (!summary?.threat_level) {
    issues.push('Missing threat level assessment');
  }
  if (!summary?.top_priorities?.length || summary.top_priorities.length < 5) {
    issues.push('Need at least 5 top priority actions');
  }
  if (summary?.readiness_score === undefined) {
    issues.push('Missing readiness score');
  }
  
  return {
    section: 'Executive Summary',
    complete: issues.length === 0,
    issues,
    pageEstimate: 2,
  };
}

function validateHouseholdProfile(plan: any, interview: any): SectionValidation {
  const issues: string[] = [];
  const profile = plan?.household_profile;
  
  const expectedMembers = interview?.household_size || 1;
  const actualMembers = profile?.member_details?.length || 0;
  
  if (actualMembers < expectedMembers) {
    issues.push(`Expected ${expectedMembers} household members, found ${actualMembers}`);
  }
  if (!profile?.vulnerability_matrix?.length) {
    issues.push('Missing vulnerability matrix');
  }
  if (!profile?.skills_inventory?.length) {
    issues.push('Missing skills inventory');
  }
  
  return {
    section: 'Household Profile',
    complete: issues.length === 0,
    issues,
    pageEstimate: 2,
  };
}

function validateRiskAssessment(plan: any): SectionValidation {
  const issues: string[] = [];
  const risks = plan?.risk_assessment || [];
  
  if (risks.length < 6) {
    issues.push(`Only ${risks.length} hazards analyzed, need at least 6`);
  }
  
  // Check each risk has required fields
  risks.forEach((risk: any, idx: number) => {
    if (!risk.hazard_name) {
      issues.push(`Risk ${idx + 1} missing hazard name`);
    }
    if (!risk.local_risk_level) {
      issues.push(`${risk.hazard_name || `Risk ${idx + 1}`} missing risk level`);
    }
  });
  
  return {
    section: 'Risk Assessment',
    complete: issues.length === 0,
    issues,
    pageEstimate: Math.max(4, Math.ceil(risks.length / 3)),
  };
}

function validateEmergencyServices(plan: any): SectionValidation {
  const issues: string[] = [];
  const services = plan?.emergency_services;
  
  if (!services?.hospitals?.length) {
    issues.push('No hospitals listed');
  }
  if (!services?.shelters?.length) {
    issues.push('No shelters listed');
  }
  if (!services?.utilities) {
    issues.push('Missing utility contacts');
  }
  
  return {
    section: 'Emergency Services',
    complete: issues.length === 0,
    issues,
    pageEstimate: 3,
  };
}

function validateEmergencyProcedures(plan: any, interview: any): SectionValidation {
  const issues: string[] = [];
  const procedures = plan?.emergency_procedures_deep || plan?.emergency_procedures || [];
  const requiredTypes = interview?.emergency_types || [];
  
  if (procedures.length < 6) {
    issues.push(`Only ${procedures.length} procedures, need at least 6`);
  }
  
  // Check each procedure has required steps
  procedures.forEach((proc: any) => {
    if (!proc.immediate_actions?.length) {
      issues.push(`${proc.hazard_type || 'Procedure'} missing immediate actions`);
    }
  });
  
  // Check coverage of selected emergency types
  const coveredTypes = new Set(procedures.map((p: any) => p.hazard_type?.toLowerCase()));
  requiredTypes.forEach((type: string) => {
    if (!coveredTypes.has(type.toLowerCase())) {
      issues.push(`Missing procedure for: ${type}`);
    }
  });
  
  return {
    section: 'Emergency Procedures',
    complete: issues.length === 0,
    issues,
    pageEstimate: Math.max(6, procedures.length),
  };
}

function validateSuppliesChecklist(plan: any): SectionValidation {
  const issues: string[] = [];
  const supplies = plan?.emergency_supplies;
  
  if (!supplies?.basic_kit?.length) {
    issues.push('Missing basic emergency kit');
  }
  if (!supplies?.water_supply) {
    issues.push('Missing water supply guidance');
  }
  if (!supplies?.food_supply?.length) {
    issues.push('Missing food supply list');
  }
  
  return {
    section: 'Supplies Checklist',
    complete: issues.length === 0,
    issues,
    pageEstimate: 3,
  };
}

function validateFinancialSection(plan: any): SectionValidation {
  const issues: string[] = [];
  const financial = plan?.financial_preparedness;
  
  if (!financial?.insurance_checklist?.length) {
    issues.push('Missing insurance checklist');
  }
  if (!financial?.document_backup?.length) {
    issues.push('Missing document backup guidance');
  }
  
  return {
    section: 'Financial Preparedness',
    complete: issues.length === 0,
    issues,
    pageEstimate: 1,
  };
}

function validateRecoverySection(plan: any): SectionValidation {
  const issues: string[] = [];
  const recovery = plan?.recovery_planning;
  
  if (!recovery?.immediate_post_disaster?.length) {
    issues.push('Missing post-disaster steps');
  }
  if (!recovery?.fema_assistance) {
    issues.push('Missing FEMA assistance info');
  }
  
  return {
    section: 'Recovery Planning',
    complete: issues.length === 0,
    issues,
    pageEstimate: 2,
  };
}

function validateCalendarSection(plan: any): SectionValidation {
  const issues: string[] = [];
  const calendar = plan?.monthly_calendar;
  
  if (!calendar?.months?.length || calendar.months.length < 12) {
    issues.push('Calendar should have 12 months');
  }
  
  return {
    section: 'Monthly Calendar',
    complete: issues.length === 0,
    issues,
    pageEstimate: 1,
  };
}

function validateQuickReference(plan: any): SectionValidation {
  const issues: string[] = [];
  const quickRef = plan?.quick_reference;
  
  if (!quickRef?.emergency_numbers?.length) {
    issues.push('Missing emergency numbers');
  }
  if (!quickRef?.evacuation_checklist?.length) {
    issues.push('Missing evacuation checklist');
  }
  
  return {
    section: 'Quick Reference',
    complete: issues.length === 0,
    issues,
    pageEstimate: 1,
  };
}

function validateWalletCards(plan: any): SectionValidation {
  const issues: string[] = [];
  const cards = plan?.wallet_cards;
  
  if (!cards?.length || cards.length < 3) {
    issues.push('Need at least 3 wallet cards');
  }
  
  return {
    section: 'Wallet Cards',
    complete: issues.length === 0,
    issues,
    pageEstimate: 1,
  };
}

function validateLocalResources(plan: any): SectionValidation {
  const issues: string[] = [];
  const resources = plan?.local_resources;
  
  if (!resources?.length) {
    issues.push('Missing local resources directory');
  }
  
  return {
    section: 'Local Resources',
    complete: issues.length === 0,
    issues,
    pageEstimate: 2,
  };
}

function validateCrossReferences(plan: any): string[] {
  const errors: string[] = [];
  
  // Check shelter references in evacuation match services
  const serviceShelters = new Set(
    (plan?.emergency_services?.shelters || []).map((s: any) => s.name?.toLowerCase())
  );
  
  const evacShelters = plan?.evacuation_plan?.shelter_options || [];
  evacShelters.forEach((shelter: any) => {
    if (shelter.name && !serviceShelters.has(shelter.name.toLowerCase())) {
      errors.push(`Evacuation shelter "${shelter.name}" not in services directory`);
    }
  });
  
  // Check phone numbers are consistent
  // (Additional cross-reference checks can be added here)
  
  return errors;
}

function generateRecommendations(
  sections: SectionValidation[], 
  pageCount: number,
  interview: any
): string[] {
  const recommendations: string[] = [];
  
  if (pageCount < PAGE_COUNT_TARGET) {
    recommendations.push(`Add ${PAGE_COUNT_TARGET - pageCount} more pages to meet target`);
  }
  
  const incompleteSections = sections.filter(s => !s.complete);
  if (incompleteSections.length > 0) {
    recommendations.push(`Complete these sections: ${incompleteSections.map(s => s.section).join(', ')}`);
  }
  
  // Household-specific recommendations
  if (interview?.pets?.length) {
    recommendations.push('Ensure pet evacuation procedures are detailed');
  }
  if (interview?.household_ages?.some((age: number) => age >= 65)) {
    recommendations.push('Include mobility assistance procedures for elderly members');
  }
  if (interview?.medical_needs?.medications?.length) {
    recommendations.push('Verify medication backup plans are included');
  }
  
  return recommendations;
}

function calculateReadinessScore(
  sections: SectionValidation[],
  typesCovered: number,
  typesRequired: number
): number {
  const sectionScore = (sections.filter(s => s.complete).length / sections.length) * 50;
  const coverageScore = (Math.min(typesCovered, typesRequired) / typesRequired) * 50;
  return Math.round(sectionScore + coverageScore);
}
